import React, { useEffect, useState, useCallback } from 'react';
import { isNewVersionAvailable } from '../utils/versionCheck';
import './UpdateNotifier.css';

// The installed PWA (manifest.json "display": "standalone") is often resumed
// from a suspended background state rather than freshly navigated to, so the
// browser never re-requests index.html and a stale bundle can keep running
// indefinitely — nginx's no-cache headers on index.html never get a chance to
// matter because no request is made. This component surfaces two signals as
// one visible prompt (CRA's SW default just console.logs its half, and a
// resumed PWA may make neither request on its own):
//   1. version.json's buildId — changes on every deploy, the reliable signal.
//   2. the service worker installing a new version — rare (this SW only
//      handles push notifications) but free to also cover.
// A prompt, never a silent reload: this is a data-entry app, so forcing a
// refresh out from under someone mid-form would drop unsaved work. The user
// decides when it's safe to tap Refresh.
const CHECK_INTERVAL_MS = 15 * 60 * 1000; // covers a tab left open without ever losing focus

const UpdateNotifier = () => {
    const [updateAvailable, setUpdateAvailable] = useState(false);

    useEffect(() => {
        const handleUpdate = () => setUpdateAvailable(true);
        window.addEventListener('sw-update-available', handleUpdate);
        return () => window.removeEventListener('sw-update-available', handleUpdate);
    }, []);

    useEffect(() => {
        const checkForUpdate = () => {
            isNewVersionAvailable().then((isNew) => {
                if (isNew) setUpdateAvailable(true);
            });

            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.getRegistration()
                    .then((registration) => registration?.update())
                    .catch(() => {});
            }
        };

        const handleVisibility = () => {
            if (document.visibilityState === 'visible') checkForUpdate();
        };

        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('focus', checkForUpdate);
        const intervalId = setInterval(checkForUpdate, CHECK_INTERVAL_MS);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('focus', checkForUpdate);
            clearInterval(intervalId);
        };
    }, []);

    const handleRefresh = useCallback(() => {
        window.location.reload();
    }, []);

    if (!updateAvailable) return null;

    return (
        <div className="update-notifier" role="status">
            <span className="update-notifier__text">A new version of PrimeOffice is available.</span>
            <button type="button" className="update-notifier__button" onClick={handleRefresh}>
                Refresh
            </button>
        </div>
    );
};

export default UpdateNotifier;
